import React from "react";
import { CheckCircle2, ChevronRight, Sparkles, X } from "lucide-react";
import { useOnboardingExperience } from "./OnboardingExperienceProvider";
import { cn } from "../ui/utils";

function NavbarProgressIndicator({
  suppressClick = false,
  isDragging = false,
  onMouseDownCapture,
  onTouchStartCapture,
  onOpen,
  onClose,
  variant = "floating",
}) {
  const {
    modalCompletedSteps,
    modalTotalSteps,
    modalProgressPercentage,
    openModal,
    shouldShowIndicator,
    showCompletionIndicator,
  } = useOnboardingExperience();

  if (!shouldShowIndicator && !showCompletionIndicator) {
    return null;
  }

  const isHeaderVariant = variant === "header";
  const isSidebarVariant = variant === "sidebar";
  const isFloatingVariant = !isHeaderVariant && !isSidebarVariant;
  const progressWidth = `${modalProgressPercentage}%`;
  const label = showCompletionIndicator
    ? "Setup complete"
    : `Setup ${modalCompletedSteps}/${modalTotalSteps}`;
  const showFloatingCloseButton =
    isFloatingVariant && typeof onClose === "function";

  const indicatorButton = (
    <button
      type="button"
      onMouseDownCapture={onMouseDownCapture}
      onTouchStartCapture={onTouchStartCapture}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (suppressClick) {
          return;
        }
        if (typeof onOpen === "function") {
          onOpen();
        }
        openModal();
      }}
      onMouseDown={(event) => event.stopPropagation()}
      className={cn(
        "group flex w-full items-center text-left transition-all",
        isHeaderVariant
          ? "min-w-[150px] max-w-[210px] gap-2 rounded-xl border border-sky-300/25 bg-white/10 px-2.5 py-2 shadow-[0_10px_24px_-18px_rgba(14,165,233,0.95)] backdrop-blur hover:border-sky-200/45 hover:bg-white/14 dark:border-cyan-300/20 dark:bg-white/5 dark:shadow-[0_10px_24px_-18px_rgba(34,211,238,0.9)] dark:hover:border-cyan-200/40 dark:hover:bg-white/10"
          : isSidebarVariant
            ? "w-full gap-2.5 rounded-2xl border border-white/10 bg-white/5 px-3.5 py-3 shadow-[0_14px_30px_-20px_rgba(14,165,233,0.9)] backdrop-blur hover:border-sky-300/35 hover:bg-white/10 dark:border-white/10 dark:bg-white/5 dark:hover:border-cyan-300/35 dark:hover:bg-white/10"
            : "w-full gap-3 rounded-2xl border border-sky-200/90 bg-white/95 px-3 py-3 shadow-[0_18px_38px_-24px_rgba(37,99,235,0.7)] backdrop-blur hover:border-sky-300 hover:shadow-[0_20px_42px_-24px_rgba(37,99,235,0.82)] dark:border-cyan-400/35 dark:bg-[#111827]/95 dark:shadow-[0_18px_38px_-24px_rgba(34,211,238,0.8)] dark:hover:border-cyan-300/55 dark:hover:shadow-[0_20px_42px_-24px_rgba(34,211,238,0.95)]",
        isFloatingVariant && (isDragging ? "cursor-grabbing" : "cursor-grab")
      )}
      aria-label={showCompletionIndicator ? "Open setup complete message" : "Open onboarding checklist"}
    >
      <span
        className={cn(
          "flex items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-sm dark:from-cyan-300 dark:to-sky-500 dark:text-slate-950",
          isHeaderVariant ? "h-8 w-8 rounded-lg" : isSidebarVariant ? "h-9 w-9 rounded-xl" : "h-10 w-10"
        )}
      >
        {showCompletionIndicator ? (
          <CheckCircle2 size={isHeaderVariant ? 16 : isSidebarVariant ? 17 : 18} />
        ) : (
          <Sparkles size={isHeaderVariant ? 16 : isSidebarVariant ? 17 : 18} />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block font-semibold uppercase tracking-[0.18em]",
            isHeaderVariant
              ? "text-[9px] text-sky-100/75 dark:text-cyan-200/70"
              : isSidebarVariant
                ? "text-[10px] text-sky-100/70 dark:text-cyan-200/70"
                : "text-[11px] text-sky-700 dark:text-cyan-300"
          )}
        >
          {isHeaderVariant ? "Onboarding" : "Setup Guide"}
        </span>
        <span
          className={cn(
            "block truncate font-semibold",
            isHeaderVariant
              ? "mt-0.5 text-sm text-white dark:text-slate-100"
              : isSidebarVariant
                ? "mt-1 text-sm text-white dark:text-slate-100"
                : "mt-1 text-sm text-slate-900 dark:text-slate-100"
          )}
        >
          {label}
        </span>

        {!showCompletionIndicator && (
          <span
            className={cn(
              "block overflow-hidden rounded-full",
              isHeaderVariant
                ? "mt-1.5 h-1.5 bg-white/20 dark:bg-cyan-500/15"
                : isSidebarVariant
                  ? "mt-2 h-1.5 bg-white/15 dark:bg-cyan-500/15"
                  : "mt-2 h-2 bg-sky-100 dark:bg-cyan-500/15"
            )}
          >
            <span
              className="block h-full rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-violet-500 transition-all dark:from-cyan-300 dark:via-sky-400 dark:to-indigo-400"
              style={{ width: progressWidth }}
            />
          </span>
        )}
      </span>

      <span
        className={cn(
          "flex items-center transition-transform group-hover:translate-x-0.5",
          isHeaderVariant
            ? "text-sky-100/70 dark:text-cyan-200/70"
            : isSidebarVariant
              ? "text-sky-100/65 dark:text-cyan-200/65"
              : "text-slate-400 dark:text-slate-500"
        )}
      >
        <ChevronRight size={isHeaderVariant ? 16 : isSidebarVariant ? 16 : 18} />
      </span>
    </button>
  );

  if (!showFloatingCloseButton) {
    return indicatorButton;
  }

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
        className="absolute -right-1.5 -top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white"
        aria-label="Close setup guide"
      >
        <X size={12} />
      </button>
      {indicatorButton}
    </div>
  );
}

export default NavbarProgressIndicator;
